import { ThunkDispatch } from "@reduxjs/toolkit";
import { MaybePromise } from "@reduxjs/toolkit/dist/query/tsHelpers";
import { FormInstance, MenuProps } from "antd";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import React from "react";

export type MenuItem = Required<MenuProps>["items"][number];

/**
 * Actions
 */

export interface ILayout {
	isAuthenticated: boolean;
}

export interface IDefaultHeader {
	setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
	isCollapsed: boolean;
}

export interface ISidemenu {
	isCollapsed: boolean;
}

export interface IForm {
	email: string;
	password: string;
}

export interface ILogin {
	isAuthenticated: boolean;
	name?: string;
	error: null | string;
	email: string;
	password: string;
	label?: string;
}
export interface IApp {
	user: object;
	user_courses: any[];
}
export interface IGeneralState {
	app: { user: IUser };
	login: ILogin;
}

/**
 * Entities
 */

export interface IUser {
	email: string;
	username: string;
	name: string;
	id: any;
	label: string;
}
export interface ICourse {
	ageFilter: string;
	author: string;
	description: string;
	id: any;
	percent: number;
	title: string;
	tags: string[];
	modules: {
		title: string;
		description: string;
		percent: number;
		classes: {
			title: string;
			description: string;
			classLink: string;
		}[];
		id: any;
	}[];
}

export interface IClass {
	id: string;
	module_id: string;
	title: string;
	description: string;
	image?: string;
	image_description?: string;
	status: string;
	visibility: string;
	is_active: boolean;
	created_at: string;
	updated_at: string;
}

export interface IBlock {
	id: any;
	class_id: string;
	type: string;
	order: number;
	content: string;
	is_active: boolean;
}

export interface IProgress {
	total_class: number;
	finished_class: number;
	progress: number;
}

export interface IAddUsers {
	form: FormInstance<any>;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	currentImgURL: string;
	setCurrentImgURL: React.Dispatch<React.SetStateAction<string>>;
	userStatus: boolean;
	setUserStatus: React.Dispatch<React.SetStateAction<boolean>>;
	userRoles: CheckboxValueType[];
	setUserRoles: React.Dispatch<React.SetStateAction<CheckboxValueType[]>>;
	setBirthDate?: React.Dispatch<React.SetStateAction<string>>;
	birthdate?: string;
	setUsers?: React.Dispatch<React.SetStateAction<any[] | undefined>>;
}

export interface IAddCourses {
	form: FormInstance<any>;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	currentImgURL: string;
	setCurrentImgURL: React.Dispatch<React.SetStateAction<string>>;
	courseStatus: boolean;
	setCourseStatus: React.Dispatch<React.SetStateAction<boolean>>;
	setModules?: React.Dispatch<React.SetStateAction<any[] | undefined>>;
	setCourses?: React.Dispatch<React.SetStateAction<any[] | undefined>>;
	courseId?: string;
}

export interface IAddNews {
	form: FormInstance<any>;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	currentImgURL: string;
	setCurrentImgURL: React.Dispatch<React.SetStateAction<string>>;
	newsStatus: boolean;
	setNewsStatus: React.Dispatch<React.SetStateAction<boolean>>;
	newsIsHighlight: boolean;
	setNewsIsHighlight: React.Dispatch<React.SetStateAction<boolean>>;
	setNews?: React.Dispatch<React.SetStateAction<any[] | undefined>>;
}

export interface IAddProduct {
	form: FormInstance<any>;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	currentImgURL: string;
	setCurrentImgURL: React.Dispatch<React.SetStateAction<string>>;
	productStatus: boolean;
	setProductStatus: React.Dispatch<React.SetStateAction<boolean>>;
	productIsHighlight: boolean;
	setProductIsHighlight: React.Dispatch<React.SetStateAction<boolean>>;
	setProducts?: React.Dispatch<React.SetStateAction<any[] | undefined>>;
}

export interface IAddBlocks {
	form: FormInstance<any>;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	currentClassInfo: any;
	files: { [k: string]: string };
	setFiles: React.Dispatch<React.SetStateAction<{ [k: string]: string }>>;
	courseStatus: boolean;
	setCourseStatus: React.Dispatch<React.SetStateAction<boolean>>;
	setBlocks: React.Dispatch<React.SetStateAction<any[]>>;
	setFromCreate: React.Dispatch<React.SetStateAction<boolean>>;
	setProcessing: React.Dispatch<React.SetStateAction<boolean>>;
	fromCreate: boolean;
}

export interface IEditBlocks {
	form: FormInstance<any>;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
	files: { [k: string]: string };
	setFiles: React.Dispatch<React.SetStateAction<{ [k: string]: string }>>;
	courseStatus: boolean;
	setCourseStatus: React.Dispatch<React.SetStateAction<boolean>>;
	setBlocks: React.Dispatch<React.SetStateAction<any[]>>;
	setProcessing: React.Dispatch<React.SetStateAction<boolean>>;
	setFromCreate: React.Dispatch<React.SetStateAction<boolean>>;
	blocks: any[];
	setSelectedBlockId?: React.Dispatch<React.SetStateAction<number>>;
	selectedBlockId: number;
	fromCreate: boolean;
}

export interface IAddClasses extends IAddCourses {
	blocks: any[];
	setSelectedBlockId: React.Dispatch<React.SetStateAction<number>>;
	setBlocks: React.Dispatch<React.SetStateAction<any[]>>;
	setIsOpenBlock: React.Dispatch<React.SetStateAction<boolean>>;
	setIsOpenEditBlock: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface IAddStudents extends IAddCourses {
	currentCourseData: DataCourse;
	setIsOpenNewStudentModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface DataType {
	key: string;
	name: string;
	age: number;
	address: string;
	tags: string[];
}

export interface CourseDataType {
	key: string;
	name: string;
	created_at: string;
	updated_at: string;
	status: string;
	tags: string[];
}

export interface DataClass {
	title: string;
	description: string;
	content_blocks: number;
	created_at: string;
	author: string;
	updated_at: string;
	id: any;
	status: boolean;
}

export interface DataModule {
	title: string;
	description: string;
	classes_length: number;
	created_at: string;
	author: string;
	updated_at: string;
	status: boolean;
	id: any;
	classes: DataClass[];
}

export interface DataCourse {
	key: string;
	title: string;
	updated_at: string;
	status: string;
	created_at: string;
	tags: string[];
	id: any;
	modules: DataModule[];
}

export interface IFeedBack {
	empty: string;
	error: string;
	success: string;
}

export interface IPagination {
	total: number;
	per_page: number;
	first_page_url: boolean;
	current_page: number;
	next_page_url: string | null;
	last_page: number;
}

export interface IGenericResponse<T = any> {
	data: T;
	debug: Array<{}>;
	feedback: IFeedBack;
	generated_in?: string;
	pagination?: IPagination;
	total?: number;
	_links?: Array<{}>;
}

export interface IClassResponse<T = IClass> {
	classes: T;
}

export type BaseQueryFn<
	Args = any,
	Result = unknown,
	Error = unknown,
	DefinitionExtraOptions = {},
	Meta = {}
> = (
	args: Args,
	api: BaseQueryApi,
	extraOptions: DefinitionExtraOptions
) => MaybePromise<QueryReturnValue<Result, Error, Meta>>;

export interface BaseQueryApi {
	signal: AbortSignal;
	dispatch: ThunkDispatch<any, any, any>;
	getState: () => unknown;
}

export type QueryReturnValue<T = unknown, E = unknown, M = unknown> =
	| {
			error: E;
			data?: undefined;
			meta?: M;
	  }
	| {
			error?: undefined;
			data: T;
			meta?: M;
	  };

export const statusTypes = {
	publish: "Publicado",
	pending: "Pendente",
} as { [k: string]: string };
